import { autoUpdate, offset, shift, useFloating } from "@floating-ui/react";
import { faChevronDown, faShareNodes } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { analyticsFiltersAtom } from "./state";
import { filtersToUrlParams } from "./utils/urlParams";

export const ShareControl = () => {
    const filters = useAtomValue(analyticsFiltersAtom);
    const [, copyToClipboard] = useCopyToClipboard();
    const [textCopied, setTextCopied] = useState(false);

    // Floating UI setup for popover positioning
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-end",
        middleware: [
            offset({
                mainAxis: 3,
                crossAxis: 0,
            }),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    // Generate shareable URL with current filters
    const shareableUrl = useMemo(() => {
        const params = filtersToUrlParams(filters);
        const baseUrl = window.location.origin + window.location.pathname;
        return `${baseUrl}?${params.toString()}`;
    }, [filters]);

    const handleCopy = () => {
        copyToClipboard(shareableUrl);
        setTextCopied(true);
        setTimeout(() => {
            setTextCopied(false);
        }, 600);
    };

    return (
        <div ref={refs.setReference}>
            <Popover>
                {() => (
                    <>
                        <Popover.Button className="flex items-center gap-2 px-3 h-8 text-sm border border-ae-gray-250 rounded bg-white hover:bg-ae-gray-100">
                            <FontAwesomeIcon
                                icon={faShareNodes}
                                className="w-4 text-ae-blue-550"
                            />
                            <span className="text-neutral-600 hidden xl:block">
                                Share
                            </span>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="w-3"
                            />
                        </Popover.Button>
                        <Popover.Panel
                            ref={refs.setFloating}
                            style={floatingStyles}
                            className="w-96 rounded-md border-2 border-ae-slate-300 drop-shadow z-50 bg-white p-3"
                        >
                            <div className="flex flex-col gap-3">
                                <p className="text-sm font-bold text-neutral-700">
                                    Share Analytics View
                                </p>
                                <p className="text-xs text-neutral-500">
                                    Copy this URL to share your current
                                    analytics filters with others.
                                </p>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={shareableUrl}
                                        onClick={(e: any) => e.target.select()}
                                        readOnly
                                        className="flex-1 px-3 py-2 text-sm border border-ae-gray-250 rounded focus:ring-2 focus:ring-ae-blue-550 focus:outline-none"
                                    />
                                    <button
                                        onClick={handleCopy}
                                        className="px-4 py-2 text-sm font-medium text-white bg-ae-blue-550 rounded hover:bg-ae-blue-600 transition-colors"
                                    >
                                        {textCopied ? "Copied!" : "Copy"}
                                    </button>
                                </div>
                            </div>
                        </Popover.Panel>
                    </>
                )}
            </Popover>
        </div>
    );
};
